import { useTranslation } from 'react-i18next'
import { Bot, User, AlertTriangle, Terminal, Search, Zap } from 'lucide-react'

function renderContent(text) {
  if (typeof text !== 'string') return text

  const parts = text.split(/(\*\*[^*\n]+?\*\*|'[^'\n]+')/g)

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={i} className="font-semibold text-theme-primary">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith("'") && part.endsWith("'") && part.length > 2) {
      return (
        <span key={i} className="text-orange-400">
          {part}
        </span>
      )
    }
    return part
  })
}

function renderThinkingContent(text) {
  if (typeof text !== 'string') return text

  const parts = text.split(/(\*\*[^*\n]+?\*\*|'[^'\n]+')/g)

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={i} className="font-semibold text-orange-300">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith("'") && part.endsWith("'") && part.length > 2) {
      return (
        <span key={i} className="text-orange-300 font-medium">
          {part}
        </span>
      )
    }
    return part
  })
}

export default function TerminalAIMessage({ message, onRetryAsLite }) {
  const { t } = useTranslation()
  const { role, content } = message

  if (role === 'user') {
    return (
      <div className="flex gap-2 justify-end">
        <div className="max-w-[85%] rounded-xl rounded-br-sm bg-theme-primary px-3 py-2 text-sm text-white">
          <p className="whitespace-pre-wrap break-words">{content}</p>
        </div>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-theme-primary/80">
          <User className="h-3.5 w-3.5 text-white" />
        </div>
      </div>
    )
  }

  if (role === 'error') {
    return (
      <div className="flex gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-900/50">
          <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
        </div>
        <div className="max-w-[85%] rounded-xl rounded-bl-sm bg-red-900/30 border border-red-800/50 px-3 py-2 text-sm text-red-300">
          <p className="whitespace-pre-wrap break-words">{content}</p>
          {message.suggestLiteAgent && onRetryAsLite && (
            <button
              onClick={onRetryAsLite}
              className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-700/50 bg-amber-900/30 px-2.5 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-900/50 transition"
            >
              <Zap className="h-3 w-3" />
              {t('terminalAi.retryAsLite')}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (role === 'system') {
    return (
      <div className="flex items-center gap-2 px-2 py-1">
        <Terminal className="h-3.5 w-3.5 text-slate-500" />
        <p className="text-xs text-slate-500 font-mono">{content}</p>
      </div>
    )
  }

  if (role === 'thinking') {
    return (
      <div className="flex gap-2">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-900/30">
          <Search className="h-3 w-3 text-orange-400" />
        </div>
        <div className="max-w-[85%] rounded-lg bg-orange-950/30 border border-orange-800/30 px-3 py-1.5 text-xs italic">
          <p className="whitespace-pre-wrap break-words text-orange-300/80">
            {renderThinkingContent(content)}
          </p>
        </div>
      </div>
    )
  }

  // assistant
  return (
    <div className="flex gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-900/50">
        <Bot className="h-3.5 w-3.5 text-emerald-400" />
      </div>
      <div className="max-w-[85%] rounded-xl rounded-bl-sm bg-slate-700/60 px-3 py-2 text-sm text-slate-200">
        <p className="whitespace-pre-wrap break-words">{renderContent(content)}</p>
      </div>
    </div>
  )
}
